import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { pct, fmtCount, share } from './objectiveFormat';

/**
 * A single horizontal bar split into labelled segments, with an optional legend.
 *
 * Segments with a zero value are dropped from the bar but kept in the legend —
 * "0 delayed" is information worth showing, a 0-width sliver is not.
 */
const StackedBar = ({ segments, total, height = 10, showLegend = true, className = '' }) => {
  const visible = useMemo(
    () => segments.filter((segment) => Number(segment.value) > 0),
    [segments]
  );

  const hasData = Number(total) > 0 && visible.length > 0;

  return (
    <div className={className}>
      <div
        className="tw-flex tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-slate-100"
        style={{ height }}
        role="img"
        aria-label={segments.map((s) => `${s.label}: ${fmtCount(s.value)}`).join(', ')}
      >
        {hasData && visible.map((segment) => (
          <div
            key={segment.key}
            title={`${segment.label}: ${fmtCount(segment.value)} (${pct(share(segment.value, total))})`}
            style={{ width: `${share(segment.value, total) * 100}%`, backgroundColor: segment.color }}
          />
        ))}
      </div>

      {showLegend && (
        <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1">
          {segments.map((segment) => (
            <span key={segment.key} className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-slate-500">
              <span className="tw-h-2 tw-w-2 tw-flex-shrink-0 tw-rounded-sm" style={{ backgroundColor: segment.color }} />
              {segment.label}
              <span className="tw-font-semibold tw-tabular-nums tw-text-slate-700">{fmtCount(segment.value)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

StackedBar.propTypes = {
  segments: PropTypes.arrayOf(PropTypes.shape({
    key:   PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.number,
    color: PropTypes.string.isRequired,
  })).isRequired,
  total:      PropTypes.number,
  height:     PropTypes.number,
  showLegend: PropTypes.bool,
  className:  PropTypes.string,
};

export default memo(StackedBar);
